"use client";

import { useMemo, useRef, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { type Columns, zipColumns } from "@/components/tools/example-columns";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  blankQuote,
  buildLedeDrafts,
  buildNewsParts,
  checkMessage,
  checkNews,
  LEDE_FRAMES,
  MAX_QUOTES,
  type NewsInput,
  type NewsQuote,
  newsText,
} from "@/lib/tool-kit/lit-essays/news-article";

/** English strings. A translated content file overrides these through `ui`. */
export const NEWS_ARTICLE_TEMPLATE_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  headlineHeading: "Headline, byline, and dateline",
  headlineLabel: "Headline",
  headlinePlaceholder: "e.g. Maple Ridge students raise $4,200 for food pantry",
  headlineHint:
    "Present tense, active voice, no final period. Capitalize only the first word and proper nouns.",
  bylineLabel: "Byline",
  bylinePlaceholder: "e.g. By Jordan Lee",
  cityLabel: "Dateline city (optional)",
  cityPlaceholder: "e.g. Maple Ridge",
  stateLabel: "State or region (optional)",
  statePlaceholder: "e.g. Mich.",
  statesHint: "Use your style guide’s abbreviation for the state.",
  factsHeading: "The five Ws",
  whoLabel: "Who",
  whoPlaceholder: "e.g. Students at Maple Ridge Middle School",
  whatLabel: "What happened — past tense",
  whatPlaceholder: "e.g. raised $4,200 for the Eastside Food Pantry",
  whenLabel: "When — day or date",
  whenPlaceholder: "e.g. Friday",
  whenHint:
    "Use the day of the week for anything within a week; avoid “yesterday” and “today”.",
  whereLabel: "Where — with its preposition (optional)",
  wherePlaceholder: "e.g. at the school’s spring car wash",
  whyLabel: "Why — with its leading word (optional)",
  whyPlaceholder: "e.g. to help families through the winter",
  ledeHeading: "The lede",
  ledeDraftsHeading: "Lede drafts",
  ledeDraftsIntro:
    "Built from the five Ws in fixed orders — honest templates, no AI. Move the time element next to the verb if it reads better.",
  ledeDraftsNeed: "Add the who and the what to see lede drafts.",
  ledeDraftLabels: ["Who, what, when", "With the place", "With the reason"],
  useLede: "Use this lede",
  copy: "Copy",
  copied: "Copied!",
  ledeLabel: "Your lede (leave blank to use the first draft)",
  ledePlaceholder: "One sentence with the most important facts.",
  bodyHeading: "The rest of the story",
  nutGrafLabel: "Nut graf — why this story matters",
  nutGrafPlaceholder: "The wider point: why the reader should care.",
  detailsLabel: "Key details — one paragraph per line",
  detailsPlaceholder: "About 60 students washed 140 cars over four hours.",
  detailsHint: "Include the how here. Put the next most important fact first.",
  quotesHeading: "Quotations",
  quoteLegend: "Quotation {n}",
  quoteTextLabel: "What the person said, exactly",
  quoteTextPlaceholder: "Copy the words exactly as spoken or written.",
  quoteSourceLabel: "Who said it — name and title",
  quoteSourcePlaceholder: "e.g. eighth-grader Priya Desai",
  addQuote: "Add a quotation",
  removeQuote: "Remove",
  removeQuoteLabel: "Remove quotation {n}",
  backgroundLabel: "Background — one paragraph per line",
  backgroundPlaceholder: "Context a reader needs: what led to this.",
  nextLabel: "What happens next (optional)",
  nextPlaceholder: "The next step, event, or date.",
  storyHeading: "Your news story",
  showLabels: "Show what each paragraph does",
  copyStory: "Copy the story",
  partLabels: {
    lede: "Lede",
    nut: "Nut graf",
    detail: "Detail",
    quote: "Quotation",
    background: "Background",
    next: "What’s next",
  },
  prompts: {
    lede: "Lede — answer who, what, and when in one sentence.",
    nut: "Nut graf — say why this story matters to the reader.",
    detail: "Detail — add the next most important facts, including how.",
    quote:
      "Quotation — add something a person said, with their name and title.",
    background: "Background — add context that explains how we got here.",
    next: "What’s next — add the next step.",
  },
  missing: { headline: "[Headline]", source: "[who said it]" },
  said: "said",
  datelineDash: "—",
  pyramidHeading: "The inverted pyramid",
  pyramidLayers: [
    "Most important: who, what, when, where",
    "Important details, quotations, and why it matters",
    "Background and context, which an editor could cut first",
  ],
  pyramidCaption:
    "Facts are ordered by importance, not by time, so the story can be cut from the bottom without losing the news.",
  checksHeading: "Check your story",
  checksIntro:
    "Checks on form, not accuracy. They cannot tell you whether the story is true or newsworthy.",
  checksNeed: "Add the who and the what (or write a lede) to run the checks.",
  pass: "Pass",
  lookAgain: "Look again",
  checks: {
    ledeLength: {
      pass: "Lede word count: {count}. Within the usual range of about 35 words or fewer.",
      flag: "Lede word count: {count}. Most newsroom guides keep ledes to about 35 words or fewer.",
    },
    ledeSentence: {
      pass: "The lede is a single sentence.",
      flag: "The lede runs to {count} sentences. Put the main fact in one and move the rest down.",
    },
    relativeTime: {
      pass: "No relative time words such as today or yesterday.",
      flag: "Uses a word like today or yesterday. Print news style uses the day of the week instead; confirm with your style guide.",
    },
    paragraphs: {
      pass: "Paragraphs are short. The longest has {count} words.",
      flag: "One paragraph has {count} words. News paragraphs are usually one or two sentences.",
    },
    headline: {
      pass: "The headline is short and has no final period.",
      flag: "Headlines are short and take no final period. Yours has {count} words, or ends in a period, or is missing.",
    },
    nutGraf: {
      pass: "The story has a nut graf.",
      flag: "No nut graf yet. A sentence on why the story matters gives readers a reason to keep going.",
    },
  },
  frames: LEDE_FRAMES,
  example: {
    headline: "Maple Ridge students raise $4,200 for food pantry",
    byline: "By Jordan Lee",
    city: "Maple Ridge",
    state: "Mich.",
    who: "Students at Maple Ridge Middle School",
    what: "raised $4,200 for the Eastside Food Pantry",
    when: "Friday",
    where: "at the school’s spring car wash",
    why: "to help families through the winter",
    lede: "",
    nutGraf:
      "The pantry says requests for help have risen by about a third since last winter, and donations usually fall after the holidays.",
    details:
      "About 60 students washed 140 cars over four hours in the school parking lot.\nLocal businesses matched the donations up to $1,000.",
    background:
      "The school has held the car wash every spring since 2019.\nThe pantry serves about 300 families a month.",
    next: "The class plans a canned-food drive in November.",
  },
  exampleQuotes: {
    text: [
      "We were hoping to raise $2,000. This blew us away.",
      "Every dollar turns into about four meals.",
    ],
    source: ["eighth-grader Priya Desai", "pantry director Marcus Bell"],
  } as Columns<"text" | "source">,
} as const;

type Strings = typeof NEWS_ARTICLE_TEMPLATE_STRINGS;

/** The example's quotes, text only; English fills any short translated column. */
export function newsExampleQuotes(strings: Strings): Omit<NewsQuote, "id">[] {
  return zipColumns(
    NEWS_ARTICLE_TEMPLATE_STRINGS.exampleQuotes,
    strings.exampleQuotes
  );
}
type TextKey = Exclude<keyof NewsInput, "quotes">;

const BLANK: Record<TextKey, string> = {
  headline: "",
  byline: "",
  city: "",
  state: "",
  who: "",
  what: "",
  when: "",
  where: "",
  why: "",
  lede: "",
  nutGraf: "",
  details: "",
  background: "",
  next: "",
};

const LAYER_CLASSES = [
  "w-full bg-primary/30",
  "w-3/4 bg-primary/20",
  "w-1/2 bg-primary/10",
] as const;

const BADGE =
  "inline-flex h-fit w-fit rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs";
const BADGE_FLAG =
  "inline-flex w-fit rounded-md border border-border bg-muted px-2 py-0.5 font-medium text-foreground text-xs";

function Pyramid({ strings }: { strings: Strings }) {
  return (
    <figure aria-label={strings.pyramidHeading} className="space-y-2">
      <div className="space-y-1.5">
        {strings.pyramidLayers.map((layer, index) => (
          <div
            className={`mx-auto rounded-md border border-border px-3 py-2 text-center text-foreground text-xs ${LAYER_CLASSES[index]}`}
            key={layer}
          >
            {layer}
          </div>
        ))}
      </div>
      <figcaption className={HINT}>{strings.pyramidCaption}</figcaption>
    </figure>
  );
}

export default function NewsArticleTemplateWidget() {
  const strings = useToolUi(NEWS_ARTICLE_TEMPLATE_STRINGS);
  const fmt = useToolNumber();
  const nextId = useRef(1);
  const [fields, setFields] = useState(BLANK);
  const [quotes, setQuotes] = useState<NewsQuote[]>([blankQuote(1)]);
  const [showLabels, setShowLabels] = useState(true);
  const input: NewsInput = useMemo(
    () => ({ ...fields, quotes }),
    [fields, quotes]
  );
  const labels = useMemo(
    () => ({ ...strings, ledeFrames: strings.frames }),
    [strings]
  );
  const drafts = buildLedeDrafts(input, strings.frames);
  const parts = useMemo(() => buildNewsParts(input, labels), [input, labels]);
  const text = newsText(input, parts, strings);
  const checks = checkNews(input, parts, strings);
  const hasLede = parts.some((part) => part.kind === "lede" && !part.prompt);
  const update = (key: TextKey) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const field = (
    key: TextKey,
    label: string,
    placeholder: string,
    hint?: string
  ) => (
    <TextField
      hint={hint}
      id={`na-${key}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      value={fields[key]}
    />
  );
  const area = (
    key: TextKey,
    label: string,
    placeholder: string,
    rows = 2,
    hint?: string
  ) => (
    <TextAreaField
      hint={hint}
      id={`na-${key}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={fields[key]}
    />
  );
  const freshQuote = () => {
    nextId.current += 1;
    return blankQuote(nextId.current);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setFields({ ...BLANK, ...strings.example });
            setQuotes(
              newsExampleQuotes(strings).map((quote) => ({
                ...freshQuote(),
                ...quote,
              }))
            );
          }}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setFields(BLANK);
            setQuotes([freshQuote()]);
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.headlineHeading}>
        {field(
          "headline",
          strings.headlineLabel,
          strings.headlinePlaceholder,
          strings.headlineHint
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          {field("byline", strings.bylineLabel, strings.bylinePlaceholder)}
          {field("city", strings.cityLabel, strings.cityPlaceholder)}
          {field(
            "state",
            strings.stateLabel,
            strings.statePlaceholder,
            strings.statesHint
          )}
        </div>
      </Panel>

      <Panel title={strings.factsHeading}>
        {area("who", strings.whoLabel, strings.whoPlaceholder, 1)}
        {area("what", strings.whatLabel, strings.whatPlaceholder, 2)}
        <div className="grid gap-4 sm:grid-cols-3">
          {field(
            "when",
            strings.whenLabel,
            strings.whenPlaceholder,
            strings.whenHint
          )}
          {field("where", strings.whereLabel, strings.wherePlaceholder)}
          {field("why", strings.whyLabel, strings.whyPlaceholder)}
        </div>
      </Panel>

      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.ledeDraftsHeading}</h2>
        {drafts.length === 0 ? (
          <Notice>{strings.ledeDraftsNeed}</Notice>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              {strings.ledeDraftsIntro}
            </p>
            {drafts.map((draft, index) => (
              <div className={`${RESULT_CARD} space-y-2`} key={draft}>
                <h3 className={WIDGET_H3}>{strings.ledeDraftLabels[index]}</h3>
                <p className="text-foreground text-sm leading-relaxed">
                  {draft}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    className={BUTTON_SMALL}
                    onClick={() => update("lede")(draft)}
                    type="button"
                  >
                    {strings.useLede}
                  </button>
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copy}
                    small
                    text={draft}
                  />
                </div>
              </div>
            ))}
          </>
        )}
      </section>

      <Panel title={strings.ledeHeading}>
        {area("lede", strings.ledeLabel, strings.ledePlaceholder, 2)}
      </Panel>

      <Panel title={strings.bodyHeading}>
        {area("nutGraf", strings.nutGrafLabel, strings.nutGrafPlaceholder)}
        {area(
          "details",
          strings.detailsLabel,
          strings.detailsPlaceholder,
          3,
          strings.detailsHint
        )}
      </Panel>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.quotesHeading}</h2>
        {quotes.map((quote, index) => (
          <fieldset className={`${CARD} space-y-4`} key={quote.id}>
            <legend className="px-1 font-medium text-foreground text-sm">
              {t(strings.quoteLegend, { n: fmt(index + 1) })}
            </legend>
            <TextAreaField
              id={`na-quote-${quote.id}`}
              label={strings.quoteTextLabel}
              onChange={(value) =>
                setQuotes((previous) =>
                  previous.map((entry) =>
                    entry.id === quote.id ? { ...entry, text: value } : entry
                  )
                )
              }
              placeholder={strings.quoteTextPlaceholder}
              prose
              rows={2}
              value={quote.text}
            />
            <TextField
              id={`na-source-${quote.id}`}
              label={strings.quoteSourceLabel}
              onChange={(value) =>
                setQuotes((previous) =>
                  previous.map((entry) =>
                    entry.id === quote.id ? { ...entry, source: value } : entry
                  )
                )
              }
              placeholder={strings.quoteSourcePlaceholder}
              value={quote.source}
            />
            {quotes.length > 1 ? (
              <button
                aria-label={t(strings.removeQuoteLabel, { n: fmt(index + 1) })}
                className={BUTTON_SMALL}
                onClick={() =>
                  setQuotes((previous) =>
                    previous.filter((entry) => entry.id !== quote.id)
                  )
                }
                type="button"
              >
                {strings.removeQuote}
              </button>
            ) : null}
          </fieldset>
        ))}
        {quotes.length < MAX_QUOTES ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setQuotes((previous) => [...previous, freshQuote()])}
            type="button"
          >
            {strings.addQuote}
          </button>
        ) : null}
      </section>

      <Panel>
        {area(
          "background",
          strings.backgroundLabel,
          strings.backgroundPlaceholder,
          3
        )}
        {area("next", strings.nextLabel, strings.nextPlaceholder, 1)}
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.storyHeading}</h2>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyStory}
            small
            text={text}
          />
        </div>
        <Checkbox
          checked={showLabels}
          label={strings.showLabels}
          onChange={setShowLabels}
        />
        <article className="space-y-4 rounded-lg border border-border bg-card p-5 sm:p-8">
          <h3 className="font-semibold text-2xl text-foreground leading-tight">
            {input.headline.trim() === ""
              ? strings.missing.headline
              : input.headline.trim()}
          </h3>
          {input.byline.trim() === "" ? null : (
            <p className="text-muted-foreground text-sm">
              {input.byline.trim()}
            </p>
          )}
          {parts.map((part, index) => (
            <div
              className="grid gap-1 sm:grid-cols-[7rem_1fr] sm:gap-3"
              // Parts are positional; two details can be word-for-word equal.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional paragraphs
              key={index}
            >
              {showLabels ? (
                <span className={BADGE}>{strings.partLabels[part.kind]}</span>
              ) : null}
              <p
                className={
                  part.prompt
                    ? "rounded-md border border-primary/40 border-dashed bg-primary/5 px-3 py-2 text-muted-foreground text-sm italic"
                    : "text-foreground text-sm leading-relaxed sm:col-start-2"
                }
              >
                {part.text}
              </p>
            </div>
          ))}
        </article>
        <Pyramid strings={strings} />
      </section>

      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.checksHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.checksIntro}</p>
        {hasLede ? (
          <ul className="space-y-2">
            {checks.map((check) => (
              <li
                className="grid gap-1 sm:grid-cols-[6.5rem_1fr] sm:gap-3"
                key={check.id}
              >
                <span className={check.ok ? BADGE : BADGE_FLAG}>
                  {check.ok ? strings.pass : strings.lookAgain}
                </span>
                <span className="text-foreground text-sm leading-relaxed">
                  {checkMessage(
                    check.ok
                      ? strings.checks[check.id].pass
                      : strings.checks[check.id].flag,
                    check,
                    fmt
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <Notice>{strings.checksNeed}</Notice>
        )}
      </section>
    </div>
  );
}
